import get from "lodash/get";
import intersectionWith from "lodash/intersectionWith";

/**
 * Brad B (6/10/2020)
 *
 * Shop has a unified concept of "available products"
 * i.e. the set of default inventory items representing which products a given work
 * is available to be purchased on. These are always retrieved from IHS.
 *
 * Presently, we always want this list of products to appear in the same order/groupings
 * defined below. This ordering responsibility should be moved outside of shop app itself
 * in the future.
 *
 * New products will go at the bottom.
 */

/**
 * Alex N (24/06/2020): On the range page (new artwork page),
 * we're moving away from the availableProducts endpoint on monolith
 * to IHS in order to get the of inventory items ids based on a work id. By doing so, we:
 * - simplify the integration
 * - render the page faster
 * - are able to render private works when an artist access it
 *
 * But we're losing the product ordering done through IA categories.
 * Ideally, each team would maintain a categorization that makes sense for what
 * they're trying to achieve but we're not ready to make this call yet.
 * So we're just mimicing the order that was provided beforehand.
 * New products will go at the bottom.
 * Mapping from productTypeId to product name can be found here: https://product-types-explorer.prod.bubblesof.red/productTypes
 *
 */
export const AVAILABLE_PRODUCTS_ORDERING = [
  /** mens */
  "chrissy-chungus",
  "grouse-chips",
  "cactus-bizzo",
  "ripper-rissole",
  "whinging-magpie",
  "aggro-emu",
  "outback-brolly",
  "top-grub",
  "sooky-sanga",
  "fair-cuppa",
  "jumping-boot",
  "shouty-pav",
  "beaut-razoo",
  "cluey-kookaburra",
  "tall-larrikin",
  "unreal-chinwag",
  "cheeky-cockatoo",
  "bonza-lamington",
  /** womens */
  "dodgy-dooverlackie",
  "minty-fizzer",
  "dinkum-bandicoot",
  "wonky-mozzie",
  "peckish-flannel",
  "shirty-shemozzle",
  "fair-bizzo",
  "frostie-esky",
  "googy-jaffle",
  "flaming-ute",
  /** stickers */
  "sticky-runners",
  "lofty-lifesavers",
  "dodgy-bonnet",
  "ropeable-gumtree",
  "overland-bikkies",
  /** device cases */
  "top-jaffle",
  "double-magpie",
  "rough-sultana",
  "lovely-dingo",
  "cranky-beetroot",
  "whinging-rissole",
  "wagging-grub",
  "bush-brolly",
  "bush-magpie",
  "furphy-fiddlesticks",
  "tassie-wallaroo",
  /** print */
  "shifting-brolly",
  "strewn-joey",
  "exy-scrub",
  "outback-mate",
  "googy-veggies",
  "top-dash",
  "fair-garage",
  "crooked-lamington",
  "beaut-flannel",
  "shouty-flannel",
  "toey-footy",
  /** decor (home living) */
  "barracking-ute",
  "drain-doco",
  "ridgy-crumpet",
  "country-dill",
  "brass-postie",
  "shouty-pressies",
  "icky-garage",
  "frostie-sunnies",
  "tinnie-telly",
  "bush-fizzer",
  /** kids */
  "big-wheelie",
  "decent-cuppa",
  "chewy-drongo",
  "tall-sock",
  "cracker-kangaroo",
  "aussie-hooroo",
  /** accessories */
  "aggro-clanger",
  "minty-serviette",
  "choccy-swag",
  "wagging-shemozzle",
  "brass-boglin",
  "heaps-freshie",
  "iffy-lippy", // mask
  "defo-freshie", // mask
  "clucky-billy", // teen mask
  "stoked-bickie", // kids mask
  "jelly-drop", // pin
  "unreal-bit",
  "big-chockie",
  "beaut-galah",
  "chewy-runners",
  "devo-croc",
  "sooky-chinwag",
  /** stationery */
  "frostie-brizzie",
  "ripper-brat",
  "whinging-koala",
  /** rest */
  "choccy-tucker",
  "gnarly-seesaw",
  "bluey-gromit", // die cut magnet
];

export const orderAvailableProducts = (inventoryItems) => {
  return inventoryItems.sort((a, b) => {
    return (
      AVAILABLE_PRODUCTS_ORDERING.indexOf(get(a, "productTypeId")) -
      AVAILABLE_PRODUCTS_ORDERING.indexOf(get(b, "productTypeId"))
    );
  });
};

const canonicalGroups = {
  STICKERS: ["sticky-runners", "lofty-lifesavers", "overland-bikkies"],
  TSHIRTS: [
    "cluey-kookaburra",
    "grouse-chips",
    "wonky-mozzie",
    "outback-brolly",
    "jumping-boot",
    "shouty-pav",
    "tall-larrikin",
    "cheeky-cockatoo",
    "bonza-lamington",
    "cactus-bizzo",
    "ripper-rissole",
    "unreal-chinwag",
    "flaming-ute",
    "dinkum-bandicoot",
    "sooky-sanga",
    "googy-jaffle",
    "peckish-flannel",
    "minty-fizzer",
    "top-grub",
  ],
  IPHONE_CASES: [
    "cranky-beetroot",
    "lovely-dingo",
    "whinging-rissole",
    "rough-sultana",
  ],
  SAMSUNG_CASES: [
    "bush-magpie",
    "tassie-wallaroo",
    "bush-brolly",
    "furphy-fiddlesticks",
  ],
  MUGS: ["wagging-shemozzle", "beaut-galah"],
  KIDS_CLOTHES: [
    "chewy-drongo",
    "tall-sock",
    "cracker-kangaroo",
    "decent-cuppa",
  ],
  // Note that not all masks are here, only the co-listed ones.
  MASKS: ["defo-freshie", "clucky-billy"],
};

export const canonicalInventoryItem = (inventoryItem, availableProducts) => {
  if (!inventoryItem) return null;
  if (!Array.isArray(availableProducts) || availableProducts.length === 0)
    return null;

  const productTypeId = get(inventoryItem, "productTypeId");

  const canonicalProductTypeId = Object.values(canonicalGroups).reduce(
    (memo, preferredProductTypeIds) => {
      if (preferredProductTypeIds.includes(productTypeId)) {
        const [intersectedProductTypeId] = intersectionWith(
          preferredProductTypeIds,
          availableProducts,
          (ptId, ap) => {
            return ptId === get(ap, "productTypeId");
          }
        );

        return intersectedProductTypeId;
      }

      return memo;
    },
    productTypeId
  );

  return availableProducts.find(
    (ap) => get(ap, "productTypeId") === canonicalProductTypeId
  );
};
